import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { formattedDate } from '@/utils/time';

import Table from '@/components/Table';
import useOrderQuery from '@/hooks/order/useOrderQuery';
import {
  ERequestedStatusType,
  Order,
  RequestedOrdersParams
} from '@/apis/order/interface';
import _ from 'lodash';
import { ReactComponent as FileRemove } from '@/assets/images/common/FileRemove.svg';
import { formatToIconOrderType, orderTableStatus } from '@/utils/tableFormat';
import { formatPhoneDefault } from '@/utils/form';
import { useRecoilState } from 'recoil';
import NoticeModal from '@/components/NoticeModal';
import { MAIN_NOTICE, neverSeeAgainState } from '@/recoil/neverSeeAgain';
import SearchHeader from './containers/SearchHeader';
import moment from 'moment';
import { NoticeList } from '@/assets/data/noticeList';
import useAgreementQuery from '@/hooks/agreement/useAgreementQuery';
import TextComponent from '@/components/TextComponent';
import { toast } from 'react-toastify';

const RequestedOrdersPage = () => {
  const history = useHistory();
  const { requestedOrdersQuery, orderReceivedMutation } = useOrderQuery();
  const { getAgreementsChecked } = useAgreementQuery();

  const listType = 'requested';
  const [body, setBody] = useState<RequestedOrdersParams>({
    page: 1,
    limit: 10,
    startDate: moment().subtract(7, 'd').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    status: ERequestedStatusType.READY,
    sort: null
  });
  const { data: orderData } = requestedOrdersQuery(body);
  const { data: isCheckedAllAgreement } = getAgreementsChecked({
    agreementType: 'PHARMACIST_TERMS_AGREEMENT'
  });

  const [noticeModal, setNoticeModal] = useState(true);
  const [neverSeeAgain, setNeverSeeAgain] = useRecoilState(neverSeeAgainState);
  const [isNeedAgreement, setIsNeedAgreement] = useState<boolean>(false);

  //Table props
  const headers = [
    { name: '상태', flex: 1, alias: 'orderStatus' },
    { name: '수령방법', flex: 1, alias: 'orderType' },
    { name: '접수 요청시각', flex: 1.2, alias: 'createdAt' },
    { name: '처방전 확인', flex: 1.5, alias: 'sendAt' },
    { name: '성함', flex: 0.8, alias: 'username' },
    { name: '전화번호', flex: 1.5, alias: 'phone' },
    { name: '주소', flex: 4, alias: 'address', left: true },
    { name: '비고', flex: 1.5, alias: 'isPrescriptionRevised' }
  ];

  const rowDataFormat = (item: Order, alias: string) => {
    const roadAddress =
      item.orderType === 'delivery' && item.agencyType === 'barogo_today';
    switch (alias) {
      case 'orderType':
        return formatToIconOrderType(item.orderType, item.agencyType);
      case 'inflowType':
        return item.inflowType === 'diagnosis' ? '일반진료' : '신청약';
      case 'createdAt':
        return formattedDate(item.createdAt!, 'HH:mm');
      case 'username':
        return item.username;
      case 'phone':
        return formatPhoneDefault(item.phone);
      case 'address':
        return `${roadAddress ? item.roadAddress : item.jibunAddress}${
          item.detailAddress ? ', ' + item.detailAddress : ''
        }`;
      case 'orderStatus':
        return orderTableStatus(item);
      case 'isPrescriptionRevised':
        if (item.isPrescriptionRevised) {
          return (
            <PrescriptionRevisedNoticeText>
              <FileRemove style={{ width: 16, height: 16, margin: 4 }} />
              처방전수정
            </PrescriptionRevisedNoticeText>
          );
        }
        return;
    }
  };

  const goDetailPage = (id: number, item?: Order) => {
    if (!_.isEmpty(item)) {
      if (!item?.orderId) return;

      if (!item?.receivedAt) {
        orderReceivedMutation.mutate(item.orderId);
      }

      history.push({ pathname: `/${listType}/${item.orderId}` });
      if (toast.isActive(item.orderId)) toast.dismiss(item.orderId);
    }
  };

  const highLightCheck = (item?: Order) => {
    return item?.receivedAt === null;
  };

  const setNeverSeeAgainNotice = () => {
    setNeverSeeAgain({
      ...neverSeeAgain,
      notice: MAIN_NOTICE
    });
    closeNoticeModal();
  };

  const goNoticeDetailPage = () => {
    setNeverSeeAgain({
      ...neverSeeAgain,
      lastestNotice: MAIN_NOTICE
    });

    history.push(`/notice/${NoticeList[0].id}`);
  };

  const goTermAgreePage = () => {
    history.push(`/termAgree`);
  };

  const closeNoticeModal = () => {
    setNoticeModal(false);
  };

  const isNoticeModalVisible =
    MAIN_NOTICE !== neverSeeAgain.notice ? noticeModal : false;

  useEffect(() => {
    if (isCheckedAllAgreement === false) setIsNeedAgreement(true);
    else setIsNeedAgreement(false);
  }, [isCheckedAllAgreement]);

  return (
    <>
      <Wrapper>
        <SearchHeader body={body} setBody={setBody} />
        <Table
          data={orderData?.list || []}
          headers={headers}
          rowDataFormat={rowDataFormat}
          page={body.page}
          totalCount={orderData?.pageable.totalCount || 0}
          setPage={(page: number) => {
            setBody({ ...body, page });
          }}
          itemsCountPerPage={body.limit!}
          onClick={goDetailPage}
          highLightCheck={highLightCheck}
        />
        {isNeedAgreement && (
          <NoticeModal>
            <NoticeModal.Content>
              <img
                style={{ maxHeight: '20vh', maxWidth: '40vh' }}
                src={require('../../assets/images/agreement/agreementModal.png')}
              />
            </NoticeModal.Content>
            <NoticeModal.Bottom>
              <BottomButton border onClick={goTermAgreePage}>
                <BottomButtonText>동의하러 가기</BottomButtonText>
              </BottomButton>
            </NoticeModal.Bottom>
          </NoticeModal>
        )}
        {!isNeedAgreement && isNoticeModalVisible && (
          <NoticeModal>
            <NoticeModal.Header
              logo
              closeButton
              onCloseButtonClick={closeNoticeModal}
            />
            <NoticeModal.Content>
              <img
                style={{ width: '100%', maxWidth: '45vw', maxHeight: '60vh' }}
                src={require('../../assets/images/popup/alternativePopup.png')}
              />
            </NoticeModal.Content>
            <NoticeModal.Bottom>
              <BottomButton border onClick={setNeverSeeAgainNotice}>
                <BottomButtonText>다시 보지 않기</BottomButtonText>
              </BottomButton>
              {/* 공지 배포시에 아래 주석해제 필요 */}
              <BottomButton onClick={goNoticeDetailPage}>
                <BottomButtonText>자세히 보기</BottomButtonText>
              </BottomButton>
            </NoticeModal.Bottom>
          </NoticeModal>
        )}
      </Wrapper>
    </>
  );
};

export default RequestedOrdersPage;

const Wrapper = styled.div`
  width: 100%;

  ${({ theme }) => theme.media.threeQuarter`
    width: 100%;
  `};
`;

const BottomButton = styled.div<{ border?: boolean }>`
  flex: 1;
  text-align: center;
  padding: 18px 0;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;

  ${({ border }) =>
    border &&
    `
    border-right-color: #efefef;
    border-right-style: solid;
    border-right-width: 1px;
    `}
  &:hover {
    background: #f8f8f8;
    cursor: pointer;
  }
`;

const BottomButtonText = styled(TextComponent)`
  font-size: 18px;
  font-weight: 600;
`;

const PrescriptionRevisedNoticeText = styled.span`
  ${({ theme }) => theme.DF_TYPOGRAPHY.UI16M};
  color: ${({ theme }) => theme.DF_COLOR.RED700};
  display: inline-flex;
  align-items: center;
`;
